import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {CooldownManager} from "./cooldown-manager";
import {FlowDecision, FlowManager} from "./flow-manager";
import {DialogRegistry} from "./dialog-registry";
import {DialogConfig, DialogRequest, OrchestratorContext} from "./types";

/** Reasons a request can be rejected/deferred — useful for events & debugging. */
export type DenyReason =
    | "cooldown"
    | "max-displays"
    | "forbidden-route"
    | "route-not-allowed"
    | "role-not-allowed"
    | "requires-auth"
    | "dependency-unmet"
    | "flow-blocked"
    | "flow-queued"
    | "can-show-false";

export type PolicyDecision =
    | {kind: "admit"}
    | {kind: "wait"; reason: DenyReason; dependencies?: DialogsEnum[]}
    | {kind: "reject"; reason: DenyReason};

/**
 * Pure, side-effect-free evaluation of whether a request may be admitted given
 * the current context. The orchestrator owns *actions* (open/close/enqueue);
 * the policy engine only *decides*. This split keeps every rule unit-testable
 * without standing up the whole engine.
 *
 *  - `reject` → drop the request (cooldown/cap exhausted, route forbidden…).
 *  - `wait`   → keep it queued and re-evaluate later (auth pending, dependency,
 *               flow lock, transient canShow=false).
 *  - `admit`  → safe to present now.
 */
export class PolicyEngine {
    constructor(
        private readonly registry: DialogRegistry,
        private readonly cooldowns: CooldownManager,
        private readonly flows: FlowManager,
    ) {}

    /** Context-aware priority; defaults to 0 when no resolver is configured. */
    priority(request: DialogRequest, ctx: OrchestratorContext): number {
        const config = this.registry.get(request.id);
        return config.priority?.(ctx, request) ?? 0;
    }

    evaluate(request: DialogRequest, ctx: OrchestratorContext): PolicyDecision {
        const config = this.registry.get(request.id);

        // --- hard rejections: no point keeping these queued ---
        if (!this.cooldowns.canShow(config)) {
            // cooldown can elapse later, but maxDisplays cannot — distinguish.
            return this.cooldowns.remainingCooldown(config) > 0
                ? {kind: "wait", reason: "cooldown"}
                : {kind: "reject", reason: "max-displays"};
        }

        if (isForbiddenRoute(config, ctx.route)) {
            return {kind: "wait", reason: "forbidden-route"};
        }
        if (!isRouteAllowed(config, ctx.route)) {
            return {kind: "wait", reason: "route-not-allowed"};
        }
        if (!isRoleAllowed(config, ctx.userRole)) {
            return {kind: "reject", reason: "role-not-allowed"};
        }

        // --- auth gate (modelled as an implicit AUTH dependency) ---
        if (config.requiresAuth && !ctx.isAuthenticated) {
            return {kind: "wait", reason: "requires-auth", dependencies: [DialogsEnum.AUTH]};
        }

        // --- explicit dependencies ---
        const unmet = this.unmetDependencies(config, ctx);
        if (unmet.length) {
            return {kind: "wait", reason: "dependency-unmet", dependencies: unmet};
        }

        // --- flow locks ---
        const flowDecision: FlowDecision = this.flows.decide(config);
        if (flowDecision === "block") return {kind: "reject", reason: "flow-blocked"};
        if (flowDecision === "queue") return {kind: "wait", reason: "flow-queued"};

        // --- final, fully context-aware gate ---
        if (config.canShow && !config.canShow(ctx, request)) {
            return {kind: "wait", reason: "can-show-false"};
        }

        return {kind: "admit"};
    }

    /**
     * Dependencies are considered "met" once they are no longer active *and*
     * their semantic effect holds. For AUTH that means `isAuthenticated`; for
     * generic dialogs it means they're simply not on screen anymore (they ran).
     */
    private unmetDependencies(config: DialogConfig, ctx: OrchestratorContext): DialogsEnum[] {
        if (!config.dependencies?.length) return [];
        return config.dependencies.filter((dep) => {
            if (dep === DialogsEnum.AUTH) return !ctx.isAuthenticated;
            return ctx.activeDialogs.includes(dep);
        });
    }
}

function matchRoute(patterns: (string | RegExp)[] | undefined, route: string): boolean {
    if (!patterns?.length) return false;
    return patterns.some((p) =>
        typeof p === "string" ? route === p || route.startsWith(p) : p.test(route),
    );
}

function isForbiddenRoute(config: DialogConfig, route: string): boolean {
    return matchRoute(config.forbiddenRoutes, route);
}

function isRouteAllowed(config: DialogConfig, route: string): boolean {
    // No allowlist => allowed everywhere (subject to forbiddenRoutes above).
    if (!config.allowedRoutes?.length) return true;
    return matchRoute(config.allowedRoutes, route);
}

function isRoleAllowed(config: DialogConfig, role: string | undefined): boolean {
    if (!config.allowedRoles?.length) return true;
    return !!role && config.allowedRoles.includes(role);
}
